import React, { useEffect, useRef } from 'react';

/**
 * Six-box one-time-code input (the standard banking/2FA pattern) — used by
 * both code entries on the signing page (OtpGate and ConfirmSignModal).
 *
 * The value is a LEFT-PACKED digit string (no gaps): typing always appends at
 * the next empty box, focus is redirected there on any click, backspace pops
 * the last digit. This keeps the state a plain string the parents already
 * hold, and makes paste / iOS code-autofill trivial — whatever arrives is
 * appended digit-by-digit no matter which box received it.
 *
 * `onComplete(code)` fires the moment the sixth digit lands, so parents can
 * auto-submit — no placeholder text needed, the empty boxes are the prompt.
 */
export default function OtpCodeInput({ value, onChange, onComplete, disabled = false }) {
  const refs = useRef([]);
  const activeIndex = Math.min(value.length, 5);

  // Focus follows the value: after every accepted digit (or clear) the next
  // empty box takes focus. Guarded so it never steals focus from elsewhere —
  // it only moves focus that is already inside the group, plus the initial
  // empty render (first mount, a clear-after-wrong-code).
  useEffect(() => {
    if (disabled) return;
    const focusInside = refs.current.includes(document.activeElement);
    if (focusInside || value === '') {
      refs.current[Math.min(value.length, 5)]?.focus();
    }
  }, [disabled, value]);

  const commit = (next) => {
    const clean = next.replace(/\D/g, '').slice(0, 6);
    onChange(clean);
    if (clean.length === 6 && clean !== value) onComplete?.(clean);
  };

  return (
    <div className='flex justify-center gap-2' onPaste={(e) => {
      e.preventDefault();
      commit(e.clipboardData.getData('text') || '');
    }}>
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type='text'
          inputMode='numeric'
          // The OS offers "From Messages/Mail" autofill on the first box; the
          // append logic absorbs the full code even when it lands in one cell.
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={value[i] || ''}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of 6`}
          onFocus={() => {
            if (i !== activeIndex) refs.current[activeIndex]?.focus();
          }}
          onChange={(e) => {
            // A controlled cell reports old+typed ('1' → type '2' → '12'):
            // strip what the cell already displayed, append only the new
            // digits (one keystroke, or a full OS autofill).
            const prev = value[i] || '';
            const raw = e.target.value;
            const typed = prev && raw.startsWith(prev) ? raw.slice(prev.length) : raw;
            commit(value + typed);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              commit(value.slice(0, -1));
            }
          }}
          className='w-10 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg
            focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100
            disabled:bg-gray-50 disabled:text-gray-300'
        />
      ))}
    </div>
  );
}
